import dayjs from 'dayjs'

import {
  DateCalendar,
  DragAndDrop,
  FormField,
  FormSelect,
  Input,
  Tabs,
  Textarea,
} from '../..'
import { useUser } from '../../../context'
import { FormFieldItem } from '../../../types'

type DynamicFieldProps = {
  item: FormFieldItem
  stateFn: {
    updateInput: (
      e: React.ChangeEvent<
        HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
      >,
    ) => void
    updateState: (key: string, value: number | string | string[] | null) => void
    removeHashtag: (tag: string) => void
    handleHashtagKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void
    handleCoverUpload: (refId: string, blob: Blob) => Promise<string>
  }
  className?: string
  refId?: string
}

export const DynamicField = ({
  item,
  stateFn,
  className = undefined,
  refId,
}: DynamicFieldProps) => {
  const { onEditionMode } = useUser()
  const extraClass = item.id === 'instructions' ? 'push-to-bottom' : undefined

  const {
    updateInput,
    updateState,
    removeHashtag,
    handleHashtagKeyDown,
    handleCoverUpload,
  } = stateFn

  const commonInputProps = {
    name: item.id,
    id: item.id,
    onChange: updateInput,
    placeholder: item.placeholder,
    value: item.value as string,
    'aria-label': item.label,
    inputClass: 'input',
  }

  const field = () => {
    switch (item.type) {
      case 'select':
        return (
          <FormSelect
            id={item.id}
            onChangeFn={updateInput}
            value={item.value as string}
            opts={item.options}
            className="input"
          />
        )

      case 'input':
        return <Input {...commonInputProps} disabled={!onEditionMode} />

      case 'date':
        return (
          <DateCalendar
            id={item.id}
            referenceDate={item.value as string}
            handleDate={(date) => {
              if (!date) return
              const _date = dayjs(date).format('DD-MM-YYYY')
              updateState(item.id, _date)
            }}
            defaultValue={dayjs(item.value as string)}
            disabled={!onEditionMode}
          />
        )

      case 'text':
        return (
          <Textarea
            {...commonInputProps}
            readOnly={!onEditionMode}
            height={110}
          />
        )

      case 'tags':
        return (
          <Tabs
            id={item.id}
            tabs={item.value as string[]}
            handleTag={removeHashtag}
            inputFn={
              item.inputData && {
                ...item.inputData,
                handleHashtagKeyDown,
                inputClass: 'input',
              }
            }
          />
        )

      case 'file':
        return (
          <DragAndDrop
            type="image"
            image={item.value as string | null}
            handleImageChange={updateState}
            onEditionMode={onEditionMode}
            refId={refId ?? item.id}
            handleFileUpload={handleCoverUpload}
          />
        )
      case 'number':
        return (
          <Input
            {...commonInputProps}
            type={item.type}
            value={item.value ?? ''}
            disabled={!onEditionMode}
          />
        )
      default:
        return <div>{item.type} - Not implemented yet</div>
    }
  }

  return (
    <FormField
      id={item.id}
      label={item.label}
      key={item.id}
      className={className ?? extraClass}
    >
      {field()}
    </FormField>
  )
}
